export const translations = {
  en: {
    site: {
      meta: {
        title: 'YesMan — AI autopilot for African businesses',
        description: 'YesMan: the AI autopilot that drives business growth, from the boardroom to the field, on a single platform accessible from any browser.',
        ogImageAlt: 'YesMan — the RISE field view',
      },
      banner: {
        text: 'RISE is the AI platform for construction entrepreneurs.',
        cta: 'Discover RISE ↗',
      },
      header: {
        rise: 'RISE',
        expansion: 'Expansion',
        languageLabel: 'Langue / Language',
        cta: 'Discover RISE',
      },
      hero: {
        title: 'Your business, driven by AI for growth',
        lead: 'YesMan is building a world where businesses are steered by AI, from the boardroom to the field, on a single platform, accessible from any browser, for sustainable growth.',
        tabsLabel: 'App views',
        views: [
          {
            label: 'Field',
            alt: 'Field view: the RISE execution screen for the Villa Koffi site, with milestones, the thread with the AI specialist and uploaded proof',
          },
          {
            label: 'Leadership',
            alt: 'Leadership view: the RISE aerial view, with the specialist summary, key figures, priority actions, engineers and site progress',
          },
        ],
      },
      category: {
        title: 'A new category of AI autopilot',
        body: "Built for growth and designed for every industry, YesMan's vertical AIs are easy enough for field teams to use and autonomous enough to steer operations.",
        tiles: [
          { label: 'Aircraft', alt: 'A pilot wearing a headset looks out of the window in flight' },
          { label: 'Car', alt: 'A self-driving car fitted with sensors on a street' },
          { label: 'Robot', alt: 'An autonomous delivery robot on a sidewalk' },
          { label: 'Business', alt: 'A technician wearing an earpiece measures a metal structure in a workshop' },
        ],
        bubble: 'Measurement matches the plan. You can move on to the next step.',
      },
      world: {
        title: 'A world where every business drives growth.',
        intro: 'Everywhere in the world, businesses, from sole traders to large companies, are a key driver of economic growth. Yet they keep running into the same questions:',
        imageAlt: 'Hendrix, an engineer, looking attentively at the camera',
        greeting: "Hi, I'm YesMan",
        question: 'What do you want to build?',
        reply: 'An apartment building in Cocody',
        questions: ['which decisions to make,', 'how to handle the unexpected,', 'how to produce more and earn more.'],
        mission: 'We are building a platform that enables AI to see, understand and adapt to every new piece of information from the field and the back office, continuously steering the business toward growth.',
      },
      zeroCompromise: {
        title: 'Zero compromise: 100% leadership, 100% field',
        body: 'Our AI solutions give leadership and field teams dedicated interfaces. Upload your documents and context: your YesMan steers the business on hard facts, not assumptions.',
        imageAlt: 'Proof photo sent by the team: a new, empty playground under a blue sky',
        message: ['Playground: safety check passed.', 'Can we open?'],
        fieldName: 'Kevin · Field Project Manager',
        fieldReply: 'Everything is compliant.',
        leadName: 'Sandra · Managing Director',
        leadReply: 'We open on Saturday.',
      },
      transform: {
        title: 'Transform without disruption',
        body: 'Your YesMan works alongside your existing operations, remembering every detail of your finances, procurement, human resources and project schedules, and bringing in specialists when needed, even without skilled staff on hand.',
        imageAlt: 'Proof photo sent by the team: the site 14 tower installed, under a blue sky',
        message: ['Site 14: tower installed.', 'Shall we check the alignment before the antennas?'],
        replyName: 'Ousmane',
        reply: "Yes, let's check.",
      },
      gains: {
        title: 'Visible gains from the very first projects',
        body: 'From cities to remote sites, our AI prepares, validates and coordinates several projects at once: fewer site visits, fewer dedicated teams and lower travel costs.',
        imageAlt: 'Naima, a farm owner, at home facing the camera',
        message: ['Your harvest is on track. No need to travel.'],
        replyName: 'Naima',
        reply: "Perfect, I'll stay at the office.",
      },
      vertical: {
        title: 'AI built for each industry, not a chatbot that does everything',
        body: 'Every YesMan solution is built around the realities of a specific industry and how it operates. It supports teams, tracks what matters and steps in at the right moment to move the business forward.',
      },
      rise: {
        title: 'Custom autopilot for the most demanding operating environments',
        status: 'Available',
        sector: 'Construction',
        name: 'YesMan RISE',
        body: 'RISE is the AI platform built for construction entrepreneurs. It helps them build a leading company without operational overload, directly from a browser, with nothing to install.',
        cta: 'Discover RISE ↗',
        imageAlt: 'Alex, a site manager, hard hat under his arm, facing the camera',
        message: ['The top slab has been poured.', 'Shall we move on to the roof?'],
        replyName: 'Alex · Site Manager',
        reply: 'We start on Monday.',
      },
      expansion: {
        eyebrow: 'Expansion',
        title: 'One infrastructure, many territories.',
        homeboy: {
          sector: 'Real estate',
          name: 'Homeboy',
          body: 'An AI infrastructure that builds a network of trust around your real estate assets, from construction to remote management.',
          cta: 'Discover Homeboy →',
        },
        upcoming: 'Coming soon',
        verticals: [
          {
            title: 'Agriculture & agribusiness',
            body: 'An AI that coordinates farms, teams and production cycles, from field to distribution, to turn every season into growth.',
            tags: ['Production', 'Operations', 'Teams', 'Distribution'],
          },
          {
            title: 'Mining & industry',
            body: 'An AI that coordinates operations, maintenance and teams, even on remote sites, to make performance more predictable.',
            tags: ['Operations', 'Maintenance', 'Teams', 'Performance'],
          },
          {
            title: 'Retail & logistics',
            body: 'An AI that coordinates inventory, teams and distribution to increase your capacity to sell, prepare and deliver.',
            tags: ['Inventory', 'Teams', 'Distribution', 'Flows'],
          },
          {
            title: 'Energy & telecoms',
            body: 'An AI that coordinates installations, teams and maintenance, from cities to the most remote areas.',
            tags: ['Installations', 'Maintenance', 'Teams', 'Networks'],
          },
          {
            title: 'Water & hydraulics',
            body: 'An AI that coordinates infrastructure, teams and maintenance, from construction to operation, to keep operations running.',
            tags: ['Infrastructure', 'Operations', 'Teams', 'Maintenance'],
          },
        ],
      },
      footer: {
        title: 'An autopilot like no other',
        body: 'An intelligence that drives business growth, from the boardroom to the field, from urban projects to the most remote sites. Because in Africa, every high-performing business helps the continent grow.',
        socialLabel: 'Social media',
        backToTop: '↑ Back to top',
      },
    },
    footer: {
      tagline: 'The new standard for autonomous AI.',
      missionTitle: 'Mission',
      mission: 'We build the AI infrastructure that enables organizations to execute more, over time, with less friction. By pairing every decision-maker with an operator assistant built to last — in the field, across key roles — we create organizations capable of performing without depending on constant presence or rare expertise.',
      legalTitle: 'Legal',
      copyright: '© 2026 YESMAN OPERATIONS. ALL RIGHTS RESERVED.',
      links: {
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        cookies: 'Cookie Policy',
      },
    },
    privacy: {
      title: 'Privacy Policy',
      updated: 'Last updated: December 2025',
    },
    terms: {
      title: 'Terms of Service',
      updated: 'Last updated: June 2026',
    },
  },
  fr: {
    site: {
      meta: {
        title: "YesMan — L'infrastructure IA pour les entreprises africaines",
        description: "YesMan : l'autopilote IA qui conduit la croissance de l'entreprise, de la direction au terrain, sur une seule plateforme, depuis un simple navigateur.",
        ogImageAlt: 'YesMan — la vue terrain RISE',
      },
      banner: {
        text: 'RISE est la plateforme IA pour les entrepreneurs du BTP.',
        cta: 'Découvrir RISE ↗',
      },
      header: {
        rise: 'RISE',
        expansion: 'Expansion',
        languageLabel: 'Langue / Language',
        cta: 'Découvrir RISE',
      },
      hero: {
        title: "Votre entreprise, Pilotée par l'IA vers la croissance",
        lead: "YesMan construit un monde où l'entreprise est pilotée par l'IA, de la direction au terrain, sur une seule plateforme accessible depuis un navigateur, pour une croissance durable.",
        tabsLabel: "Vues de l'application",
        views: [
          {
            label: 'Terrain',
            alt: "Vue terrain : l'écran de conduite RISE du chantier Villa Koffi, avec les jalons, le fil d'échanges avec le spécialiste IA et les preuves déposées",
          },
          {
            label: 'Direction',
            alt: "Vue direction : la vue aérienne RISE, avec la synthèse du spécialiste, les chiffres clés, les actions prioritaires, les ingénieurs et l'avancement des chantiers",
          },
        ],
      },
      category: {
        title: "Une nouvelle catégorie d'autopilote IA",
        body: "Dédiées à la croissance et conçues pour chaque secteur, les IA métier YesMan sont assez simples pour assister les équipes terrain et suffisamment autonomes pour piloter les opérations de l'entreprise.",
        tiles: [
          { label: 'Avion', alt: "Un pilote d'avion, casque sur les oreilles, regarde par le hublot en vol" },
          { label: 'Voiture', alt: 'Une voiture autonome équipée de capteurs dans une rue' },
          { label: 'Robot', alt: 'Un robot de livraison autonome sur un trottoir' },
          { label: 'Entreprise', alt: 'Un technicien avec une oreillette mesure une structure métallique dans un atelier' },
        ],
        bubble: "Mesure conforme au plan. Vous pouvez passer à l'étape suivante.",
      },
      world: {
        title: 'Un monde où toute entreprise mène à la croissance.',
        intro: "Partout dans le monde, l'entreprise, individuelle ou formelle, est un facteur déterminant de la croissance économique. Pourtant, elle bute toujours sur les mêmes questions :",
        imageAlt: 'Hendrix, ingénieur, face à la caméra, attentif',
        greeting: 'Bonjour, je suis YesMan',
        question: 'Que voulez‑vous construire ?',
        reply: 'Un immeuble à Cocody',
        questions: ['quelle décision prendre,', 'quels imprévus affronter,', 'comment produire plus pour gagner plus.'],
        mission: "Nous construisons une plateforme qui donne à l'IA les moyens de voir, comprendre et s'adapter à chaque nouvelle information du terrain et de la vie administrative de l'entreprise, pour la piloter en continu vers la croissance.",
      },
      zeroCompromise: {
        title: 'Zéro compromis : 100 % direction, 100 % terrain',
        body: "Nos solutions IA réunissent des interfaces distinctes pour la direction et le terrain. Importez vos documents et votre contexte : votre YesMan pilote l'entreprise sur la base de faits concrets, pas de suppositions.",
        imageAlt: "Photo de preuve envoyée par l'équipe : une aire de jeux neuve et vide, sous un ciel bleu",
        message: ['Aire de jeux : sécurité validée.', 'On peut ouvrir ?'],
        fieldName: 'Kevin · Chef de projet terrain',
        fieldReply: 'Tout est conforme.',
        leadName: 'Sandra · Directrice générale',
        leadReply: 'On ouvre samedi.',
      },
      transform: {
        title: 'Transformer sans perturber',
        body: 'Sans rien bousculer, votre YesMan retient les moindres détails de vos finances, de vos approvisionnements, de vos ressources humaines et du planning de vos projets, et mobilise des spécialistes quand il le faut, même sans personnel qualifié.',
        imageAlt: "Photo de preuve envoyée par l'équipe : le pylône du site 14 monté, sous un ciel bleu",
        message: ['Site 14 : pylône monté.', "On vérifie l'alignement avant les antennes ?"],
        replyName: 'Ousmane',
        reply: 'Oui, on vérifie.',
      },
      gains: {
        title: 'Des gains visibles dès les premières missions',
        body: "De la ville aux sites reculés, notre IA prépare, valide et coordonne plusieurs projets simultanément : moins de visites, moins d'équipes dédiées et moins de frais de déplacement.",
        imageAlt: "Naima, propriétaire d'une exploitation agricole, chez elle face à la caméra",
        message: ['Vos récoltes avancent bien. Inutile de vous déplacer.'],
        replyName: 'Naima',
        reply: 'Parfait, je reste au bureau.',
      },
      vertical: {
        title: 'Une IA conçue pour chaque métier, pas un chatbot pour tout faire',
        body: "Chaque solution YesMan est conçue autour des réalités d'un secteur et de ses opérations. Elle accompagne les équipes, suit ce qui compte et intervient au bon moment pour faire avancer l'entreprise.",
      },
      rise: {
        title: 'Autopilote sur mesure pour les environnements opérationnels les plus exigeants',
        status: 'Disponible',
        sector: 'BTP',
        name: 'YesMan RISE',
        body: 'RISE est la plateforme IA dédiée aux entrepreneurs du BTP. Elle aide à développer une entreprise de référence sans surcharge opérationnelle, depuis un simple navigateur et sans installation.',
        cta: 'Découvrir RISE ↗',
        imageAlt: 'Alex, conducteur de travaux, casque sous le bras, face à la caméra',
        message: ['La dalle haute est coulée.', 'On passe à la toiture ?'],
        replyName: 'Alex · Conducteur de travaux',
        reply: 'On attaque lundi.',
      },
      expansion: {
        eyebrow: 'Expansion',
        title: 'Une même infrastructure, plusieurs territoires.',
        homeboy: {
          sector: 'Immobilier',
          name: 'Homeboy',
          body: 'Une infrastructure IA qui construit un réseau de confiance autour de votre patrimoine immobilier, de la construction à la gestion à distance.',
          cta: 'Découvrir Homeboy →',
        },
        upcoming: 'À venir',
        verticals: [
          {
            title: 'Agriculture & agro-industrie',
            body: 'Une IA qui coordonne exploitations, équipes et cycles de production, du terrain à la distribution, pour transformer chaque saison en croissance.',
            tags: ['Production', 'Opérations', 'Équipes', 'Distribution'],
          },
          {
            title: 'Mines & industries',
            body: 'Une IA qui pilote opérations, maintenance et équipes, même sur les sites reculés, pour rendre la performance plus prévisible.',
            tags: ['Opérations', 'Maintenance', 'Équipes', 'Performance'],
          },
          {
            title: 'Commerce & logistique',
            body: 'Une IA qui coordonne stocks, équipes et distribution pour augmenter votre capacité à vendre, préparer et livrer.',
            tags: ['Stocks', 'Équipes', 'Distribution', 'Flux'],
          },
          {
            title: 'Énergie & télécoms',
            body: 'Une IA qui coordonne installations, équipes et maintenance, des villes aux zones les plus isolées.',
            tags: ['Installations', 'Maintenance', 'Équipes', 'Réseaux'],
          },
          {
            title: 'Eau & hydraulique',
            body: "Une IA qui coordonne ouvrages, équipes et maintenance, de la réalisation à l'exploitation, pour assurer la continuité des opérations.",
            tags: ['Ouvrages', 'Exploitation', 'Équipes', 'Maintenance'],
          },
        ],
      },
      footer: {
        title: 'Un autopilote qui ne ressemble à aucun autre',
        body: "Une intelligence qui pilote la croissance de l'entreprise, de la direction au terrain, des projets urbains aux sites les plus reculés. Parce qu'en Afrique, chaque entreprise qui performe fait grandir tout un continent.",
        socialLabel: 'Réseaux sociaux',
        backToTop: '↑ Retour en haut de la page',
      },
    },
    footer: {
      tagline: "La nouvelle norme pour l'IA autonome.",
      missionTitle: 'Mission',
      mission: "Nous construisons l'infrastructure IA qui permet aux organisations d'exécuter davantage, sur la durée, avec moins de friction. En associant chaque décideur à un assistant opérateur conçu pour tenir — sur le terrain, dans les rôles clés — nous créons des organisations capables de performer sans dépendre de la présence constante ou de l'expertise rare.",
      legalTitle: 'Legal',
      copyright: '© 2026 YESMAN OPERATIONS. TOUS DROITS RÉSERVÉS.',
      links: {
        privacy: 'Confidentialité',
        terms: 'CGU',
        cookies: 'Cookies',
      },
    },
    privacy: {
      title: 'Politique de Confidentialité',
      updated: 'Dernière mise à jour : Décembre 2025',
    },
    terms: {
      title: "Conditions Générales d'Utilisation",
      updated: 'Dernière mise à jour : Juin 2026',
    },
  },
}
